import { apiClient } from './apiClient';
import { getPostDetail as getPostDetailFromPostApi } from '../postApi';
import { formatToKSTDateString } from '../../utils/dateUtils';

export interface ReplyData {
  reply_id: number;
  content: string;
  post_title: string;
  reply_writer_name: string;
  reply_created_at: string;
  exposed: boolean;
  post_id?: number; // 게시글 ID 추가
}

// 게시글 상세 정보 인터페이스 (실제 API 응답에 맞게 수정)
export interface PostDetailData {
  post_category: string;
  post_title: string;
  post_content: string;
  writer_name: string;
  writer_profile_image_url: string;
  created_at: string;
  updated: boolean;
  can_update: boolean;
  attachment_urls: string[];
  reply_cnt: number;
  view_cnt: number;
  lecture_id?: number;
  lecture_title?: string;
  lecture_chapter?: string;
  study_name?: string;
  replies: Array<{
    reply_id: number;
    content: string;
    writer_name: string;
    writer_profile_image_url: string;
    reply_created_at: string;
    images: string[];
    exposed: boolean;
  }>;
}

// 게시글 상세 정보 응답 인터페이스
export interface PostDetailResponse {
  code: number;
  status: string;
  message: string;
  data: PostDetailData;
}

// 댓글 목록 응답 인터페이스
export interface ReplyListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: ReplyData[];
    number: number;
    sort: {
      empty: boolean;
      sorted: boolean;
      unsorted: boolean;
    };
    first: boolean;
    last: boolean;
    numberOfElements: number;
    pageable: {
      offset: number;
      sort: {
        empty: boolean;
        sorted: boolean;
        unsorted: boolean;
      };
      paged: boolean;
      pageNumber: number;
      pageSize: number;
      unpaged: boolean;
    };
    empty: boolean;
  };
}

// 댓글 조작 응답 인터페이스
export interface ReplyActionResponse {
  code: number;
  status: string;
  message: string;
  data: ReplyData;
}

// 필터 상태 타입
export interface FilterState {
  postCategoryId: number | null;
  postId: number | null;
  fromDate: string;
  toDate: string;
  status: boolean | null; // true: 노출, false: 숨김, null: 전체
  searchTerm: string;
}

// 페이지네이션 상태 타입
export interface PaginationState {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

// API 함수들
export const adminReplyApi = {
  // 댓글 목록 조회 (검색어 유무에 따라 API 분기)
  getReplies: async (
    filter: FilterState,
    pagination: { page: number; size: number }
  ): Promise<ReplyListResponse> => {
    const params = new URLSearchParams();
    
    // 기본 페이지네이션 파라미터
    params.append('page', pagination.page.toString());
    params.append('size', pagination.size.toString());

    // 필터 파라미터들 추가
    if (filter.postCategoryId !== null) {
      params.append('postCategoryId', filter.postCategoryId.toString());
    }
    
    // 날짜 필터 (YYYY-MM-DD 형식, 둘 다 있거나 둘 다 없어야 함)
    const hasFromDate = filter.fromDate && filter.fromDate.trim() !== '';
    const hasToDate = filter.toDate && filter.toDate.trim() !== '';
    
    // 둘 다 있는 경우에만 날짜 파라미터 추가
    if (hasFromDate && hasToDate) {
      params.append('from', filter.fromDate);
      params.append('to', filter.toDate);
    }
    
    // 상태 필터 (true: 노출, false: 숨김)
    if (filter.status !== null) {
      params.append('status', filter.status.toString());
    }

    // 검색어가 있으면 list 엔드포인트의 search 파라미터로 검색
    const hasSearchQuery = filter.searchTerm && filter.searchTerm.trim() !== '';
    if (hasSearchQuery) {
      params.append('search', filter.searchTerm.trim());
    }
    const url = `/api/admin/replies/list?${params.toString()}`;

    try {
      const response = await apiClient.get(url);
      
      /// console.log('댓글 API 응답:', response.data);
      
      return response.data;
    } catch (error) {
      /// console.error('댓글 API 호출 중 예외 발생:', error);
      throw error;
    }
  },

  // 댓글 삭제 (soft delete)
  deleteReply: async (replyId: number): Promise<void> => {
    /// console.log('댓글 삭제 요청:', {replyId});
    
    try {
      const response = await apiClient.delete(`/api/admin/replies/${replyId}`);
      //  console.log('댓글 삭제 성공:', {
      //   replyId, 
      //   status: response.status,
      //   statusText: response.statusText || 'No Content'
      // });
    } catch (error) {
      /// console.error('댓글 삭제 API 호출 실패:', error);
      throw error;
    }
  },

  // 댓글 숨기기/노출 토글
  toggleReplyExposure: async (replyId: number, exposed: boolean): Promise<ReplyActionResponse> => {
    /// console.log('댓글 노출 상태 변경 요청:', {replyId, exposed});
    
    try {
      const response = await apiClient.patch(`/api/admin/replies/${replyId}/exposed?exposed=${exposed}`);
      
      /// console.log('댓글 노출 상태 변경 성공:', response.data);
      
      return response.data;
    } catch (error) {
      /// console.error('댓글 노출 상태 변경 API 호출 실패:', error);
      throw error;
    }
  },

  // 게시글 상세 정보 조회 (기존 postApi 사용)
  getPostDetail: async (postId: number): Promise<PostDetailResponse> => {
    try {
      // 기존 postApi의 getPostDetail 함수 사용
      const result = await getPostDetailFromPostApi(postId);
      
      /// console.log('게시글 상세 정보 조회 성공 (postApi):', {postId, result});
      return result;
    } catch (error) {
      /// console.error('게시글 상세 정보 조회 실패 (postApi):', error);
      throw error;
    }
  }
};

// 날짜 형식 검증 함수
const isValidDateFormat = (dateString: string): boolean => {
  const regex = /^\d{4}-\d{2}-\d{2}$/;
  if (!regex.test(dateString)) return false;
  
  const date = new Date(dateString);
  return date instanceof Date && !isNaN(date.getTime()) && dateString === formatToKSTDateString(date);
};
